import { DEVICE, DEVICE_TYPE } from "constants/index";
import { getAppVersion } from "helpers/appVersion";
import { getCookie } from "helpers/utils/cookie";
import { getActualDeviceType } from "helpers/utils/getActualDeviceType";
import { isServer } from "helpers/utils/isServer";
import { getMobileAppDeviceType } from "helpers/utils/webView";
import { logger } from "logging";
import { RANDOM_NUMBER_COOKIE_KEY, RANDOM_NUMBER_COOKIE_KEY_FOR_VARIANT } from "modules/visitorPrioritization/constants";

interface CheckIsEligibleParams {
  featureData: any;
  userData: any;
}

interface VariantData {
  name: string;
  isBaseline: boolean;
  userPercentage: number;
}

interface VariantData {
    userPercentage: number,
    minimumSupportedVersion: string,
    isEnabledForInternalUsers: boolean,
    enabledForUsers: string[],
    variants: VariantData[]
}

interface checkIsExperimentEligibleReturnType{
  isExperimentEligible: boolean, 
  mixpanelConfigExperiment?: any 
}

const compareVersion = function(version1: string, version2: string) {
  try {
    /**
     * converting version string into array
     */
    let cmpVersion1arr = version1.split(".");
    let cmpVersion2arr = version2.split(".");

    let length = Math.max(cmpVersion1arr.length, cmpVersion2arr.length);

    for (let i = 0; i < length; i++) {
      const firstVersion = parseInt(cmpVersion1arr[i]) || 0;
      const secondVersion = parseInt(cmpVersion2arr[i]) || 0;

      if (firstVersion < secondVersion) return -1;
      if (firstVersion > secondVersion) return 1;
    }
    /**
     * if version 1 and 2 both are equal;
     */
    return 1;
  } catch (error) {
    return 0;
  }
};

const checkIsEligible = (featureData: any, run: number) => {
  const { status, userPercentage } = featureData || {};
  // checking if value is number
  if (status && run <= userPercentage) {
    return true;
  }
  return false;
};

export const checkIsSwitchEnabledForDevice = (switchName: any, deviceType: string): boolean => {
  let isEnabled = false;

  if (!isServer()) {
    try {
      const enabledGlobally = switchName?.status;
      const currentVersion = getAppVersion(); // only for ios and android

      if (enabledGlobally) {
        if (deviceType == DEVICE_TYPE.DESKTOP) {
          isEnabled = switchName?.configs?.enableDesktop;
        } else if (deviceType == DEVICE_TYPE.MOBILE) {
          isEnabled = switchName?.configs?.enableMobile;
        } else if (deviceType == DEVICE_TYPE.ANDROID) {
          isEnabled = switchName?.configs?.enableAndroid?.status;
          const minimumSupportedVersion =
            switchName?.configs?.enableAndroid?.minimumSupportedVersion;

          if (isEnabled) {
            isEnabled =
              compareVersion(currentVersion, minimumSupportedVersion) === -1 || 0 ? false : true;
          }
        } else if (deviceType == DEVICE_TYPE.IOS) {
          isEnabled = switchName?.configs?.enableIos?.status;
          const minimumSupportedVersion = switchName?.configs?.enableIos?.minimumSupportedVersion;

          if (isEnabled) {
            isEnabled =
              compareVersion(currentVersion, minimumSupportedVersion) === -1 || 0 ? false : true;
          }
        }
      }
    } catch (e) {
      logger.error(e as Error, 'Error in checkIsSwitchEnabledForDevice');
    }
  }

  return isEnabled;
};

/**
 * Check if user is eligible for the experiment
 * userData is only required to be passed if we have to check if user is internal user or allowed user
 * if internal user check is not required we can pass null
 */
export const checkIsExperimentEligible = (
  key: string,
  remoteConfig: Record<string | number, any>,
  deviceType: string,
  userData?: any,
): checkIsExperimentEligibleReturnType => {
  try {
    const run = Number(getCookie(RANDOM_NUMBER_COOKIE_KEY));
    const runValueForVariant = Number(getCookie(RANDOM_NUMBER_COOKIE_KEY_FOR_VARIANT) || 0);
    const configData = remoteConfig?.AB[key] as any;
    const currentVersion = getAppVersion(); // only for ios and android
    /**
     * This config can be used to track all the checkout experiments and their status
     */
    const mixpanelConfigExperiment = {
      experiment: key,
      isEnabled : false,
      variantConfig: {},
    }
    if (!configData?.status) return { isExperimentEligible: false, mixpanelConfigExperiment };
    let deviceConfig: any = {};
    if (configData) {
      if (deviceType === DEVICE_TYPE.ANDROID) {
        deviceConfig = configData["android"];
        const minimumSupportedVersion = deviceConfig?.minimumSupportedVersion;
        const isEnabled =
          compareVersion(currentVersion, minimumSupportedVersion) === -1 || 0 ? false : true;
        deviceConfig["status"] = isEnabled;
      } else if (deviceType === DEVICE_TYPE.IOS) {
        deviceConfig = configData["ios"];
        const minimumSupportedVersion = deviceConfig?.minimumSupportedVersion;
        const isEnabled =
          compareVersion(currentVersion, minimumSupportedVersion) === -1 || 0 ? false : true;
        deviceConfig["status"] = isEnabled;
      } else if (deviceType === DEVICE_TYPE.MOBILE) {
        deviceConfig = configData["mweb"];
        deviceConfig["status"] = true; // keeping it true because we don't have any version condition for mobile
      } else {
        deviceConfig = configData["dweb"];
        deviceConfig["status"] = true; // keeping it true because we don't have any version condition for desktop
      }

      let eligibilityForInternalUsersOrAllowedUsers = checkIsEligibleForInterUsersOrAllowedUsers({
        featureData: deviceConfig,
        userData,
      });
      /**
       * if allowed for internal users or allowed users, 
       * then return the eligibility along with the mixpanel config
       */
      if (eligibilityForInternalUsersOrAllowedUsers.status) {
        mixpanelConfigExperiment.isEnabled = true;
        mixpanelConfigExperiment.variantConfig = eligibilityForInternalUsersOrAllowedUsers.config;
        return { isExperimentEligible: true, mixpanelConfigExperiment };
      }

      let eligibility = checkIsEligible(deviceConfig, run);
      /**
        if user lies in the experiment, then check if user falls in the variant user percentage
        if variant is not present, then return the eligibility, this can be a case where we have only experiment and no variant
      */
      if (eligibility && deviceConfig?.variants?.length > 0) {
        eligibility = checkIsVariantEligibleForExperiment(deviceConfig.variants, runValueForVariant);
        /**
         * Updating the mixpanelConfigExperiment data
         */
        mixpanelConfigExperiment.variantConfig = getUserVariant(deviceConfig.variants, runValueForVariant);
      }
      /**
       * update the user eligibility in mixpanelConfigExperiment data
       * it can be true or false based on the user eligibility
       */
      mixpanelConfigExperiment.isEnabled = eligibility;
      // update exp_config before returning
      return { isExperimentEligible: eligibility, mixpanelConfigExperiment };
    }
    // if error fetching key in config
  } catch (error) {
    logger.error(error as Error, `Error in checkIsExperimentEligible for ${key}`);
  }
  return { isExperimentEligible: false };
};

export const checkIsVariantEligibleForExperiment = (variants : VariantData[], runVariant: number) => {
  try {
      const variant = getUserVariant(variants, runVariant);
      /**
       * if variant is baseline, then user is not eligible for the experiment
       */
      if(variant.isBaseline){
        return false;
      }
      return true;
  } catch (error) {
    logger.error(error as Error, `Error in checkIsVariantEligibleForExperiment`);
  }
  return false;
}

/**
 * Check if user is eligible for the feature based on the user id or internal user
 */
const checkIsEligibleForInterUsersOrAllowedUsers = (
  checkIsEligibleParams: CheckIsEligibleParams
) => {
  try {
    const { featureData, userData } = checkIsEligibleParams;
    const { status, isEnabledForInternalUsers, enabledForUserIds } = featureData || {};
    /**
     * status is passed to check if user has the compatible version
     */
    if (status && userData) {
      /**
       * If any of these conditions are true, then user is eligible:
       *  1. If user is internal user and enabled for internal users
       *  2. If user id is present in enabledForUserIds
       */
      const config: any = {};
      if (isEnabledForInternalUsers && checkIsUserInternalUser(userData?.email)) {
        config.isInternalUser = true;
        return { status: true, config };
      }
      if (
        enabledForUserIds?.length > 0 &&
        checkIsUserIdEligible(userData?.id, enabledForUserIds || [])
      ) {
        config.isUserIdAllowed = true;
        return { status: true, config };
      }
    }
  } catch (error) {
    logger.error(error, `Error in checkIsEligibleForInterUsersOrAllowedUsers`);
  }
  return { status: false, config: {} };
};

/**
 * if user id has @nykaa.com, then user is internal user
 */
const checkIsUserInternalUser = (email: string) => {
  if (email) {
    return email.includes("@nykaa.com");
  }
  return false;
};

/**
 * Get the variant for the user based on the user percentage
 */
function getUserVariant(variants: VariantData[], run: number): VariantData {
  try {
    let cumulativePercentage = 0;

    for (const variant of variants) {
      const { userPercentage: variantUserPercentage } = variant || {};
      cumulativePercentage += variantUserPercentage;
      if (run <= cumulativePercentage) {
        return variant;
      }
    }

    /**
     * Fallback in case of rounding issues or misconfiguration
     * if variant sum doesn't add up to 100,then we should return the
     * baseline or 1st variant, so that experiment is enabled in such cases
     */

    return variants.find(x => x.isBaseline) || variants[0];
  } catch (error) {
    logger.error(error as Error, `Error in getUserVariant`);
    return variants[0];
  }
}

/**
 * If user Id is added for allowed users, then user is eligible
 */
const checkIsUserIdEligible = (userId: string, enabledForUserIds: string[]) => {
  if ((enabledForUserIds||[]).includes(userId)) {
    return true;
  }
  return false;
};

/**
 * Function returns edd switch enable or disable.
 */
export const checkIsEddSwitchEnabled = () => {
  if (!isServer()) {
    const { remoteConfig } = window as any;

    const device = getActualDeviceType();
    let deviceType = DEVICE_TYPE.MOBILE;

    if (device === DEVICE.MOBILE) {
      deviceType = getMobileAppDeviceType();
    } else {
      deviceType = DEVICE_TYPE.DESKTOP;
    }

    const isEddEnabled = checkIsSwitchEnabledForDevice(
      remoteConfig?.switches?.EDD,
      deviceType
    );

    return isEddEnabled;
  }

  return false;
};

/**
 * function to check is payment from address page experiment enable.
 */
export const doCheckPaymentFromAddressPageExpEnable = ({
  experiment,
  orderAmount,
  addressCount,
  isGuest,
}: {
  experiment: boolean;
  orderAmount: number;
  addressCount: number;
  isGuest: boolean;
}) => {

  //this experiment is only for new user.
  if (experiment && !isGuest && orderAmount && addressCount === 0) {
    return true;
  }

  return false;
};